import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Loader2, Plus, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { useUploadFile } from '@/hooks/use-upload-file';
import { LAYER_IMAGE_MIME_TYPES, isAcceptedMimeType } from '@/lib/upload/acceptedMimeTypes';
import {
  MAX_REVIEW_IMAGES,
  appendPrivateReviewImages,
  removeReviewImage,
  toggleReviewImageVisibility,
  type ReviewImage,
} from '@/lib/campaigns/reviewImages';

type ReviewImagesFieldProps = {
  value: ReviewImage[];
  onChange: (next: ReviewImage[]) => void;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
};

const PREFIX = 'admin.campaigns.submissions.reviewImages';

export function ReviewImagesField({
  value,
  onChange,
  disabled = false,
  onBusyChange,
}: ReviewImagesFieldProps) {
  const { t } = useTranslation();
  const { getUploadUrl } = useUploadFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Uploads are async and `value` is parent-controlled: always append to the
  // latest value (never the one captured when the upload started).
  const valueRef = useRef(value);
  valueRef.current = value;

  const mountedRef = useRef(true);
  const uploadingRef = useRef(false);
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;

  useEffect(() => {
    onBusyChange?.(uploading);
  }, [uploading, onBusyChange]);

  // If unmounted mid-upload, release the parent's busy flag so submit never
  // stays blocked, and ignore the late result.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (uploadingRef.current) onBusyChangeRef.current?.(false);
    };
  }, []);

  const locked = disabled || uploading;
  const canAddMore = value.length < MAX_REVIEW_IMAGES;

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_REVIEW_IMAGES - valueRef.current.length;
    if (remaining <= 0) {
      setError(t(`${PREFIX}.maxReached`, { max: MAX_REVIEW_IMAGES }));
      return;
    }
    const selected = Array.from(files)
      .filter((file) => isAcceptedMimeType(file.type, LAYER_IMAGE_MIME_TYPES))
      .slice(0, remaining);
    if (selected.length === 0) {
      setError(t(`${PREFIX}.invalidType`));
      return;
    }

    setError(null);
    uploadingRef.current = true;
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of selected) {
        const url = await getUploadUrl(file, undefined, {
          acceptedMimeTypes: LAYER_IMAGE_MIME_TYPES,
        });
        if (!mountedRef.current) return;
        if (!url) {
          setError(t(`${PREFIX}.uploadFailed`));
          break;
        }
        uploaded.push(url);
      }
      if (uploaded.length > 0 && mountedRef.current) {
        onChange(appendPrivateReviewImages(valueRef.current, uploaded));
      }
    } finally {
      uploadingRef.current = false;
      if (mountedRef.current) {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-2" data-testid="review-images-field">
      <Label>{t(`${PREFIX}.label`)}</Label>
      <p className="text-xs text-muted-foreground">{t(`${PREFIX}.hint`)}</p>
      <div className="flex flex-wrap gap-3">
        {value.map((image, index) => {
          const isPublic = image.visibility === 'public';
          return (
            <div key={`${image.url}-${index}`} className="w-24 space-y-1">
              <div
                className={`relative h-24 w-24 overflow-hidden rounded-md border-2 ${
                  isPublic ? 'border-primary' : 'border-dashed border-muted-foreground/60'
                }`}
              >
                <img
                  src={image.url}
                  alt=""
                  className={`h-full w-full object-cover ${isPublic ? '' : 'opacity-70'}`}
                />
                <button
                  type="button"
                  className="absolute right-1 top-1 rounded-full bg-background/90 p-0.5 text-foreground hover:bg-background disabled:opacity-50"
                  onClick={() => onChange(removeReviewImage(valueRef.current, index))}
                  disabled={locked}
                  aria-label={t(`${PREFIX}.remove`)}
                  data-testid="review-image-remove"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <button
                type="button"
                className={`flex w-full items-center justify-center gap-1 rounded-md border px-1 py-0.5 text-xs disabled:opacity-50 ${
                  isPublic
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-muted text-muted-foreground'
                }`}
                onClick={() => onChange(toggleReviewImageVisibility(valueRef.current, index))}
                disabled={locked}
                aria-pressed={isPublic}
                data-testid="review-image-visibility"
              >
                {isPublic ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {t(`${PREFIX}.${isPublic ? 'public' : 'private'}`)}
              </button>
            </div>
          );
        })}
        {canAddMore ? (
          <button
            type="button"
            className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
            onClick={() => fileInputRef.current?.click()}
            disabled={locked}
            data-testid="review-images-add"
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            {t(`${PREFIX}.add`)}
          </button>
        ) : (
          <p className="self-center text-xs text-muted-foreground">
            {t(`${PREFIX}.maxReached`, { max: MAX_REVIEW_IMAGES })}
          </p>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={LAYER_IMAGE_MIME_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={(event) => {
          void handleFiles(event.target.files);
        }}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
